import { useMemo, useState } from "react";
import { ArrowDownTrayIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { Badge, Card, Select, Spinner } from "../../../shared";
import { CardHeader } from "../../../shared/Card/CardHeader";
import {
  formatBillingDate,
  formatMoneyFromCents,
  getInvoiceAmountCents,
  getInvoiceDescription,
  getInvoiceStatusDisplay,
  groupInvoicesByYearAndMonth,
} from "./billing.utils";

/**
 * Invoice history grouped by year and month for easier customer review.
 */
export const BillingInvoiceHistory = ({
  invoices = [],
  loading = false,
  collectionMethod = "manual_invoice",
  canManagePortal = false,
  renderInvoiceActions,
  onViewInvoice,
}) => {
  const invoiceGroups = useMemo(
    () => groupInvoicesByYearAndMonth(invoices),
    [invoices]
  );
  const yearOptions = useMemo(() => {
    const years = invoiceGroups.map((group) => ({
      label: String(group.year),
      value: String(group.year),
    }));

    return [{ label: "All years", value: "all" }, ...years];
  }, [invoiceGroups]);
  const [selectedYear, setSelectedYear] = useState("all");

  const filteredGroups = useMemo(() => {
    if (selectedYear === "all") {
      return invoiceGroups;
    }

    return invoiceGroups.filter((group) => String(group.year) === selectedYear);
  }, [invoiceGroups, selectedYear]);

  const emptyMessage =
    collectionMethod === "manual_invoice"
      ? "No invoices yet. Your account team will add monthly or annual invoices here when they are issued."
      : canManagePortal
      ? "No invoices yet. Your first invoice appears after your subscription is charged."
      : "Invoices appear here after you start a subscription.";

  return (
    <Card
      header={
        <CardHeader
          title="Invoice history"
          actions={
            yearOptions.length > 2 ? (
              <div className="w-40">
                <Select
                  size="sm"
                  value={selectedYear}
                  options={yearOptions}
                  onChange={(target) => setSelectedYear(target?.value || "all")}
                />
              </div>
            ) : null
          }
        />
      }
      content={
        loading ? (
          <Spinner />
        ) : filteredGroups.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            {emptyMessage}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredGroups.map((yearGroup) => (
              <section key={yearGroup.year} className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">{yearGroup.year}</h3>
                {yearGroup.months.map((monthGroup) => (
                  <div key={`${yearGroup.year}-${monthGroup.month}`} className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {monthGroup.monthLabel} {yearGroup.year}
                    </h4>
                    <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                      {monthGroup.invoices.map((invoice) => {
                        const statusDisplay = getInvoiceStatusDisplay(invoice?.status);
                        const amountCents = getInvoiceAmountCents(invoice);
                        const viewUrl = invoice?.hosted_invoice_url;
                        const pdfUrl = invoice?.invoice_pdf;

                        return (
                          <li
                            key={invoice.id}
                            className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                          >
                            <div className="flex items-start gap-3">
                              <DocumentTextIcon className="mt-0.5 h-5 w-5 text-gray-400" />
                              <div>
                                <div className="font-medium text-gray-900">
                                  {getInvoiceDescription(invoice)}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {formatBillingDate(invoice?.created)}
                                  {invoice?.number ? ` · ${invoice.number}` : ""}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">
                                  {formatMoneyFromCents(amountCents)}
                                </div>
                                <Badge color={statusDisplay.color} size="sm">
                                  {statusDisplay.label}
                                </Badge>
                              </div>
                              <div className="flex gap-2">
                                {onViewInvoice ? (
                                  <button
                                    type="button"
                                    onClick={() => onViewInvoice(invoice)}
                                    className="text-sm font-medium text-primary-700 hover:underline"
                                  >
                                    View
                                  </button>
                                ) : null}
                                {viewUrl ? (
                                  <a
                                    href={viewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-primary-700 hover:underline"
                                  >
                                    View
                                  </a>
                                ) : null}
                                {pdfUrl ? (
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline"
                                  >
                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                    PDF
                                  </a>
                                ) : null}
                                {renderInvoiceActions ? renderInvoiceActions(invoice) : null}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )
      }
    />
  );
};
